import { T, useGT } from 'gt-react';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';

import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/** The optional `.*` toggle for callers whose search understands patterns. */
export interface TableSearchRegexToggle {
  active: boolean;
  onToggle: () => void;
  /** Flags an invalid pattern (red border) — server rejects it too. */
  invalid?: boolean;
  /** Explanation shown as a tooltip on the search box when invalid. */
  invalidMessage?: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /**
   * Renders a VS Code-style `.*` toggle inside the search box for switching
   * between substring and regex matching. Omit to hide the toggle entirely.
   */
  regexToggle?: TableSearchRegexToggle;
  className?: string;
}

/**
 * The search box a table is filtered with.
 *
 * Its own component because a list that isn't a {@link GentlyTable} — the
 * pricelist upload history, for one — still has to look like the tables beside
 * it, and the magnifier, the widths and the invalid-pattern styling are the
 * part that makes it look the same.
 */
export function TableSearchInput({
  value,
  onChange,
  placeholder,
  regexToggle,
  className,
}: Props) {
  const gt = useGT();

  return (
    <div className={cn('relative max-w-sm', className)}>
      <MagnifyingGlassIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
      <Input
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        title={regexToggle?.invalid ? regexToggle.invalidMessage : undefined}
        aria-invalid={regexToggle?.invalid}
        className={cn(
          'pl-8',
          regexToggle && 'pr-9',
          regexToggle?.invalid &&
            'border-destructive focus-visible:ring-destructive',
        )}
      />
      {regexToggle && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={regexToggle.onToggle}
              aria-pressed={regexToggle.active}
              aria-label={gt('Toggle regular expression search', {
                context: 'table search regex toggle',
              })}
              className={cn(
                'absolute right-1.5 top-1/2 -translate-y-1/2 rounded px-1 py-0.5 font-mono text-xs leading-none transition-colors',
                regexToggle.active
                  ? 'bg-primary/15 text-primary'
                  : 'text-muted-foreground hover:bg-muted',
              )}
            >
              .*
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <T context="table search regex toggle tooltip">
              Toggle regular expression search
            </T>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
